import { useRef, useState, type KeyboardEvent, type MouseEvent, type TouchEvent } from "react";
import type { RowAction } from "../utils/explorerActions";

const LONG_PRESS_MS = 500;

export interface RowMenuPosition {
  top: number;
  left: number;
}

export interface RowMenuState {
  rowKey: string;
  anchorEl: HTMLElement | null;
  anchorPosition: RowMenuPosition | null;
}

/**
 * The explorer's per-row contextual menu (right-click, the "more"
 * three-dots button, Shift+F10/ContextMenu on a focused row, a long-press
 * on touch): one hook so the table and the grid view share the exact same
 * behaviour and cannot drift apart, per row `actions` from the shared
 * action model (utils/explorerActions.ts).
 */
export function useRowMenu() {
  const [menu, setMenu] = useState<RowMenuState | null>(null);
  const longPressTimer = useRef<number | null>(null);

  function openAtElement(rowKey: string, el: HTMLElement) {
    setMenu({ rowKey, anchorEl: el, anchorPosition: null });
  }

  function openAtPosition(rowKey: string, top: number, left: number) {
    setMenu({ rowKey, anchorEl: null, anchorPosition: { top, left } });
  }

  function close() {
    setMenu(null);
  }

  function clearLongPress() {
    if (longPressTimer.current !== null) {
      window.clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }

  function handleContextMenu(
    event: MouseEvent<HTMLElement>,
    rowKey: string,
    actions: RowAction[] | undefined,
  ) {
    if (!actions?.length) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    openAtPosition(rowKey, event.clientY, event.clientX);
  }

  function handleKeyDown(
    event: KeyboardEvent<HTMLElement>,
    rowKey: string,
    actions: RowAction[] | undefined,
  ) {
    if (!actions?.length) {
      return;
    }
    if ((event.shiftKey && event.key === "F10") || event.key === "ContextMenu") {
      event.preventDefault();
      openAtElement(rowKey, event.currentTarget);
    }
  }

  function handleTouchStart(
    event: TouchEvent<HTMLElement>,
    rowKey: string,
    actions: RowAction[] | undefined,
  ) {
    if (!actions?.length) {
      return;
    }
    const touch = event.touches[0];
    clearLongPress();
    longPressTimer.current = window.setTimeout(() => {
      openAtPosition(rowKey, touch.clientY, touch.clientX);
    }, LONG_PRESS_MS);
  }

  return {
    menu,
    openAtElement,
    openAtPosition,
    close,
    clearLongPress,
    handleContextMenu,
    handleKeyDown,
    handleTouchStart,
  };
}
